<script module lang="ts">
	export type DropSelectOption = {
		/** Stable key, also used as the fallback label. */
		value: string;
		/** Primary text shown for the option. */
		label: string;
		/** Optional secondary text shown to the right of the label (e.g. a count). */
		secondaryLabel?: string;
	};
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { ChevronDownIcon } from '../../assets/icons/ChevronDownIcon';
	import { ChevronUpIcon } from '../../assets/icons/ChevronUpIcon';
	import { Button, type ButtonSize, type ButtonVariant } from '../Button';
	import { Txt } from '../Txt';

	interface Props {
		/** The selectable options. With a single option the toggle acts as a plain button. */
		options: DropSelectOption[];
		/** Fired with the chosen option when the user picks one (or when there is a single option). */
		onSelect: (option: DropSelectOption) => void;
		/** Text on the toggle button. */
		label: string;
		dataCy: string;
		variant?: ButtonVariant;
		size?: ButtonSize;
		fullWidth?: boolean;
		/**
		 * Which way the option list opens. `down` (default) renders it below the
		 * toggle; `up` renders it above, for a toggle pinned to the bottom of its
		 * container (e.g. the wishlist drawer footer).
		 */
		direction?: 'down' | 'up';
		/**
		 * When true the option list is absolutely positioned over the page (anchored
		 * to the toggle) so opening it does not shift surrounding content. The
		 * default keeps the list in flow, pushing content as it expands.
		 */
		floating?: boolean;
		class?: string;
	}

	let {
		options,
		onSelect,
		label,
		dataCy,
		variant = 'primary',
		size = 'md',
		fullWidth = true,
		direction = 'down',
		floating = false,
		...rest
	}: Props = $props();

	let open = $state(false);
	let rootElement = $state<HTMLDivElement>();

	// The list only ever shows when there is a genuine choice to make.
	let hasChoice = $derived(options.length > 1);
	let showOptions = $derived(open && hasChoice);

	// The component instance can persist across client-side navigation (e.g. the
	// series page moving between sibling series), so reset the open state whenever
	// the options change — otherwise the picker can linger open showing stale drops.
	$effect(() => {
		// Reference options so this re-runs when they change.
		void options;
		open = false;
	});

	const select = (option: DropSelectOption) => {
		open = false;
		onSelect(option);
	};

	const onToggleClick = () => {
		// Single option → act immediately; multiple → reveal the picker.
		if (options.length === 1) {
			select(options[0]);
		} else {
			open = !open;
		}
	};

	const onWindowKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Escape' && open) {
			open = false;
		}
	};

	// Close when a click lands outside the component.
	$effect(() => {
		if (!open) {
			return;
		}

		const onDocumentClick = (event: MouseEvent) => {
			if (rootElement && !rootElement.contains(event.target as Node)) {
				open = false;
			}
		};

		document.addEventListener('click', onDocumentClick);
		return () => document.removeEventListener('click', onDocumentClick);
	});
</script>

<svelte:window onkeydown={onWindowKeydown} />

{#snippet toggleButton()}
	<Button
		dataCy={`${dataCy}-drop-select`}
		{variant}
		{size}
		{fullWidth}
		class={classNames('w-full', {
			'rounded-b-none': showOptions && direction === 'down',
			'rounded-t-none': showOptions && direction === 'up'
		})}
		onclick={onToggleClick}
		aria-haspopup={hasChoice ? 'listbox' : undefined}
		aria-expanded={hasChoice ? open : undefined}
	>
		{label}
		{#snippet trailing()}
			{#if hasChoice}
				{#if open}
					<ChevronUpIcon class="ml-2 h-4 w-4" />
				{:else}
					<ChevronDownIcon class="ml-2 h-4 w-4" />
				{/if}
			{/if}
		{/snippet}
	</Button>
{/snippet}

{#snippet optionsList()}
	<div
		role="listbox"
		aria-label={label}
		class={classNames(
			'max-h-[12rem] overflow-scroll border border-gray-200 bg-gray-0',
			direction === 'down' ? 'rounded-b border-t-0' : 'rounded-t border-b-0',
			{
				// Overlay the page instead of pushing content, anchored to the toggle.
				'absolute left-0 z-10 w-full': floating,
				'top-full': floating && direction === 'down',
				'bottom-full': floating && direction === 'up'
			}
		)}
		data-cy={`${dataCy}-drop-select-options`}
	>
		{#each options as option, i (option.value)}
			<button
				type="button"
				role="option"
				aria-selected="false"
				onclick={() => select(option)}
				class="flex w-full items-center justify-between px-4 py-3 text-left outline-none hover:bg-gray-100"
				class:border-t={i > 0}
				class:border-t-gray-200={i > 0}
				data-cy={`${dataCy}-drop-select-option`}
			>
				<Txt variant="label3">{option.label}</Txt>
				{#if option.secondaryLabel}
					<Txt variant="body3" class="text-gray-500">{option.secondaryLabel}</Txt>
				{/if}
			</button>
		{/each}
	</div>
{/snippet}

<div bind:this={rootElement} class={classNames({ relative: floating }, rest.class)}>
	<!-- With `direction === 'up'` the list is rendered above the toggle so a
	     bottom-anchored picker (e.g. the wishlist drawer footer) opens upward. -->
	{#if showOptions && direction === 'up'}
		{@render optionsList()}
	{/if}

	{@render toggleButton()}

	{#if showOptions && direction === 'down'}
		{@render optionsList()}
	{/if}
</div>
